import { z } from 'zod';
import { validatePropertyExpression } from '../utils/property-expression';
/**
 * Common schema building blocks. Every stored entity is tenant-scoped,
 * ISO-8601 string-timestamped, and soft-deletable with `deletedAt: null`
 * set explicitly on create (Desk api-standards §5.5 — a missing field does
 * NOT match `where('deletedAt', '==', null)`).
 */
/** ISO-8601 timestamp string (what `new Date().toISOString()` produces). */
export const isoDateTimeSchema = z.string().datetime({ offset: true });
/**
 * Identifier string. Restricted to a URL/key-safe charset so ids can never
 * collide with the `#` and `/` delimiters used in metric row keys and
 * alert dedup keys (fail-closed at the source, not just at key-build time).
 */
export const idSchema = z
    .string()
    .min(1)
    .max(256)
    .regex(/^[A-Za-z0-9_-]+$/, 'ids may only contain letters, digits, _ and -');
/**
 * Instance identifier — same charset as idSchema, but `"-"` alone is
 * rejected: it is reserved as the no-instance sentinel in row keys and
 * dedup keys. Enforced here so nothing that validates can later fail
 * key materialization.
 */
export const instanceIdSchema = idSchema.refine((v) => v !== '-', {
    message: '"-" is reserved as the no-instance sentinel',
});
/** Tenant identifier — required everywhere, never defaulted (fail-closed).
 *  Same key-safe charset restriction as idSchema. */
export const tenantIdSchema = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9_-]+$/, 'tenant ids may only contain letters, digits, _ and -');
/** IANA timezone name, validated against the runtime's timezone database
 *  so invalid zones are rejected at write time, not at scheduling time. */
export const timezoneSchema = z
    .string()
    .min(1)
    .max(64)
    .refine((tz) => {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}, { message: 'must be a valid IANA timezone (e.g. "America/Los_Angeles")' });
/**
 * Server-managed fields present on every stored entity document.
 * These are never accepted from client input — input schemas are
 * `.strict()` and exclude them.
 */
export const baseEntityFields = {
    tenantId: tenantIdSchema,
    createdAt: isoDateTimeSchema,
    createdBy: idSchema,
    updatedAt: isoDateTimeSchema,
    lastModifiedBy: idSchema,
    /** REQUIRED null on create so soft-delete list queries match. */
    deletedAt: isoDateTimeSchema.nullable(),
    deletedBy: idSchema.nullable(),
};
export const baseEntitySchema = z.object(baseEntityFields);
/**
 * Resource/instance property maps. Keys are namespaced by origin:
 * `system.*` (server/classification-managed), `custom.*` (user-set),
 * `auto.*` (discovery-set). Values are strings by convention (LM-style).
 * The namespace is enforced so the origin of every property is always
 * unambiguous to the targeting engine and the UI.
 */
/** Canonical property key: lowercase (the expression engine matches keys
 *  case-insensitively, so mixed-case stored keys would alias) and
 *  namespaced by origin. */
export const propertyKeySchema = z
    .string()
    .regex(/^(system|custom|auto)\.[a-z0-9_][a-z0-9_.-]*$/, 'property keys must be lowercase and namespaced: system.*, custom.*, or auto.*');
export const propertyMapSchema = z.record(propertyKeySchema, z.string().max(4096));
/** Client-writable property map: `custom.*` ONLY. `system.*` and `auto.*`
 *  are server/discovery-managed — accepting them from clients would let a
 *  tenant spoof classification and inventory provenance. */
export const customPropertyMapSchema = z.record(z
    .string()
    .regex(/^custom\.[a-z0-9_][a-z0-9_.-]*$/, 'client-writable property keys must be custom.* (lowercase)'), z.string().max(4096));
/** Property keys a property-sensor may set: `system.*` only. */
export const systemPropertyKeySchema = z
    .string()
    .regex(/^system\.[a-z0-9_][a-z0-9_.-]*$/, 'property sensors set system.* keys (lowercase)');
/** Slug used in hostnames (status-page subdomains, tenant slugs). */
export const slugSchema = z
    .string()
    .min(2)
    .max(63)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'must be a lowercase DNS-label: letters, digits, hyphens; no leading/trailing hyphen');
/** A fully-qualified DNS hostname (status-page custom domains). */
export const hostnameSchema = z
    .string()
    .min(4)
    .max(253)
    .regex(/^(?=.{4,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, 'must be a valid lowercase hostname');
/**
 * Property expression (AppliesTo / rule targeting / discovery filters).
 * Syntax-validated at write time so a typo'd expression is rejected here
 * instead of silently never matching at evaluation time.
 */
export const propertyExpressionStringSchema = z
    .string()
    .min(1)
    .max(2048)
    .superRefine((expr, ctx) => {
    const result = validatePropertyExpression(expr);
    if (!result.valid) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `invalid property expression: ${result.error}`,
        });
    }
});
/**
 * A number taken from a query string. `z.coerce.number()` turns an EMPTY
 * value (`?x=`) into 0 and whitespace into 0, silently; this rejects both,
 * plus anything non-numeric or non-finite, so "absent" and "zero" stay
 * different things.
 */
export const queryNumberSchema = z
    .string()
    .trim()
    .min(1, 'must not be empty')
    .transform((v) => Number(v))
    .pipe(z.number().finite());
//# sourceMappingURL=common.schema.js.map